import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  Play,
  Pause,
  Trash2,
  Mail,
  Users,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { CampaignStatus } from '../../types/campaign';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useAppData } from '../../contexts/AppDataContext';
import { Spinner } from '../ui';
import { formatTimeAgo } from '../../utils/dateUtils';
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS } from '../../constants/campaignStatus';

interface CampaignsListProps {
  onNewCampaign?: () => void;
}

type FilterTab = 'all' | CampaignStatus;

const PAGE_SIZE = 20;

export const CampaignsList: React.FC<CampaignsListProps> = ({ onNewCampaign }) => {
  const navigate = useNavigate();
  const { refreshCampaigns: refreshSidebarCampaigns } = useAppData();
  const {
    campaigns,
    total,
    isLoading,
    listCampaigns,
    deleteCampaign,
    activateCampaign,
    pauseCampaign,
  } = useCampaigns();

  const [filterStatus, setFilterStatus] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchCampaigns = useCallback(() => {
    const params: { status?: string; limit: number; offset: number } = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };
    if (filterStatus !== 'all') {
      params.status = filterStatus;
    }
    listCampaigns(params).catch(() => {});
  }, [filterStatus, currentPage, listCampaigns]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Handle filter change - reset page and update filter together
  const handleFilterChange = useCallback((newFilter: FilterTab) => {
    if (newFilter !== filterStatus) {
      setCurrentPage(1);
      setFilterStatus(newFilter);
    }
  }, [filterStatus]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleRowClick = useCallback((campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  }, [navigate]);

  const handleActivate = useCallback(async (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    await activateCampaign(campaignId);
    fetchCampaigns();
    refreshSidebarCampaigns();
  }, [activateCampaign, fetchCampaigns, refreshSidebarCampaigns]);

  const handlePause = useCallback(async (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    await pauseCampaign(campaignId);
    fetchCampaigns();
    refreshSidebarCampaigns();
  }, [pauseCampaign, fetchCampaigns, refreshSidebarCampaigns]);

  const handleDelete = useCallback(async (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (confirm('Are you sure you want to delete this campaign?')) {
      await deleteCampaign(campaignId);
      refreshSidebarCampaigns();
      // If we deleted the last item on this page, go back a page
      if (campaigns.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        fetchCampaigns();
      }
    }
  }, [deleteCampaign, refreshSidebarCampaigns, campaigns.length, currentPage, fetchCampaigns]);

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(query) ||
      (campaign.description?.toLowerCase().includes(query) ?? false)
    );
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, total);

  const filterTabs: { id: FilterTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'paused', label: 'Paused' },
    { id: 'draft', label: 'Draft' },
    { id: 'completed', label: 'Completed' },
  ];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Filter tabs */}
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleFilterChange(tab.id)}
              className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition ${
                filterStatus === tab.id
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-52 pl-9 pr-4 py-2 text-[13px] border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-opacity-10 focus:border-neutral-300 transition"
            />
          </div>

          {/* New Campaign button */}
          <button
            onClick={onNewCampaign}
            className="flex items-center gap-2 px-3.5 py-2 bg-neutral-900 hover:bg-neutral-800 text-white text-[13px] font-medium rounded-lg transition"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Campaign</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="md" text="Loading campaigns..." />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4">
            <Mail className="w-7 h-7 text-neutral-300" />
          </div>
          <h3 className="text-[15px] font-semibold text-neutral-900 mb-1.5">
            {searchQuery ? 'No campaigns found' : 'No campaigns yet'}
          </h3>
          <p className="text-[13px] text-neutral-500 mb-5 max-w-sm">
            {searchQuery
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first campaign to start reaching out to your leads'}
          </p>
          {!searchQuery && (
            <button
              onClick={onNewCampaign}
              className="flex items-center gap-2 px-4 py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white text-[13px] font-medium rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
              Create Campaign
            </button>
          )}
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Recipients
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider hidden md:table-cell">
                  Channel
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider hidden lg:table-cell">
                  Created
                </th>
                <th className="px-4 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredCampaigns.map((campaign) => {
                const statusStyle = CAMPAIGN_STATUS_COLORS[campaign.status];
                return (
                  <tr
                    key={campaign.id}
                    onClick={() => handleRowClick(campaign.id)}
                    className="hover:bg-neutral-50 cursor-pointer transition group"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-neutral-200 flex items-center justify-center flex-shrink-0 transition-colors">
                          <Mail className="w-4 h-4 text-neutral-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-neutral-900 truncate">
                            {campaign.name}
                          </p>
                          {campaign.description && (
                            <p className="text-[11px] text-neutral-400 truncate max-w-xs">
                              {campaign.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusStyle.bg} ${statusStyle.text}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                        {CAMPAIGN_STATUS_LABELS[campaign.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 text-[13px] text-neutral-600">
                        <Users className="w-3.5 h-3.5 text-neutral-400" />
                        {campaign.recipients_count.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[13px] text-neutral-500 capitalize">
                        {campaign.channel}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-[12px] text-neutral-400">
                        {formatTimeAgo(campaign.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === campaign.id ? null : campaign.id);
                          }}
                          className="p-1.5 rounded-md hover:bg-neutral-100 transition opacity-0 group-hover:opacity-100"
                        >
                          <MoreHorizontal className="w-4 h-4 text-neutral-400" />
                        </button>
                        {openMenuId === campaign.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                              }}
                            />
                            <div className="absolute right-0 mt-1 w-40 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-20">
                              {campaign.status === 'active' ? (
                                <button
                                  onClick={(e) => handlePause(e, campaign.id)}
                                  className="w-full px-3 py-2 text-left text-[13px] text-neutral-700 hover:bg-neutral-50 flex items-center gap-2"
                                >
                                  <Pause className="w-3.5 h-3.5" />
                                  Pause
                                </button>
                              ) : campaign.status !== 'completed' && campaign.status !== 'archived' ? (
                                <button
                                  onClick={(e) => handleActivate(e, campaign.id)}
                                  className="w-full px-3 py-2 text-left text-[13px] text-neutral-700 hover:bg-neutral-50 flex items-center gap-2"
                                >
                                  <Play className="w-3.5 h-3.5" />
                                  Activate
                                </button>
                              ) : null}
                              <button
                                onClick={(e) => handleDelete(e, campaign.id)}
                                className="w-full px-3 py-2 text-left text-[13px] text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination footer */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-neutral-100 bg-neutral-50/50">
              <span className="text-[11px] text-neutral-400">
                {startItem}–{endItem} of {total}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    disabled={currentPage <= 1}
                    className="flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-[11px] text-neutral-500 px-2 tabular-nums">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage >= totalPages}
                    className="flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignsList;
